import { Link } from "react-router-dom";
import type { Business } from "@/types";
import Rating from "@/components/base/Rating";
import { categories } from "@/mocks/categories";
import { locations } from "@/mocks/locations";
import { businessPath } from "@/utils/slug";

interface BusinessCardProps {
  business: Business;
  badge?: "sponsored" | "featured" | "none";
}

export default function BusinessCard({ business, badge = "featured" }: BusinessCardProps) {
  const b = business;
  const cat = categories.find((c) => c.id === b.categoryId);
  const loc = locations.find((l) => l.id === b.locationId);
  const catTo = `/category/${cat?.slug ?? b.categoryId}`;
  const bTo = businessPath(b);
  const waNumber = b.phone.replace(/[^0-9]/g, "");

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200">
      <Link to={bTo} className="relative block h-44 w-full overflow-hidden">
        <img
          src={b.image}
          alt={`${b.name} — ${b.categoryName} in ${b.locationName}`}
          title={`${b.name} ${b.categoryName}`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {badge === "sponsored" && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-accent-300 bg-accent-100 px-2.5 py-1 text-xs font-medium leading-none text-accent-900">
              <i className="ri-megaphone-line text-[13px]"></i> Sponsored
            </span>
          )}
          {badge === "featured" && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-primary-200 bg-primary-100 px-2.5 py-1 text-xs font-medium leading-none text-primary-800">
              <i className="ri-vip-crown-2-line text-[13px]"></i> Featured
            </span>
          )}
          {b.isVerified && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-secondary-200 bg-secondary-100 px-2.5 py-1 text-xs font-medium leading-none text-secondary-900">
              <i className="ri-verified-badge-fill text-[13px]"></i> Verified
            </span>
          )}
        </div>
        {b.openNow && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-semibold text-accent-800">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span> Open Now
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <Link
          to={catTo}
          className="inline-flex w-fit max-w-full items-center gap-1.5 text-xs font-semibold text-secondary-700 transition-colors hover:text-primary-700"
        >
          <i className="ri-price-tag-3-line text-[13px]"></i>
          <span className="truncate">{b.categoryName}</span>
        </Link>
        <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={bTo} className="transition-colors hover:text-primary-700">
            {b.name}
          </Link>
        </h3>
        <p className="mt-1 flex items-start gap-1.5 text-xs text-foreground-500">
          <i className="ri-map-pin-line mt-0.5 text-secondary-500"></i>
          <span className="line-clamp-1">{b.address}</span>
        </p>
        <p className="mt-3 line-clamp-2 min-h-[40px] text-sm leading-relaxed text-foreground-600">{b.description}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {b.tags.slice(0, 3).map((t) => (
            <span key={t} className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-600">
              {t}
            </span>
          ))}
        </div>

        <div className="mt-auto">
          <div className="mt-4 flex items-center border-t border-background-200 pt-3">
            <Rating value={b.rating} count={b.reviewCount} />
          </div>
          <div className="mt-3 flex items-stretch gap-2">
            <a
              href={`tel:${b.phone.replace(/\s/g, "")}`}
              className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-xs font-semibold text-foreground-950 transition-colors duration-200 hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-phone-line text-sm"></i> Call
            </a>
            <a
              href={`https://wa.me/${waNumber}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-600 px-3 text-xs font-semibold text-background-50 transition-colors duration-200 hover:bg-accent-700 cursor-pointer"
            >
              <i className="ri-whatsapp-line text-sm"></i> WhatsApp
            </a>
          </div>
          <Link
            to={loc ? `/location/${loc.slug}` : "/directory"}
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-foreground-500 transition-colors hover:text-primary-700"
          >
            <i className="ri-map-pin-2-line"></i> More in {b.locationName}
          </Link>
        </div>
      </div>
    </article>
  );
}